import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  Country,
  ISearcherStationsProvider,
  Market,
  SearcherState,
  SearcherStation,
  Station,
  stationSearcherSelector
} from '@vueling-ng/vy-cdk-searcher';
import { catchError, combineLatest, map, Observable, of } from 'rxjs';
import { ResourcesDataService } from '../../../services/api-services/resourcesManager/resources.data-service';
import { StateManagerService } from '../../state/state-manager.service';
import { ConverterSearcherStationsService } from './converter-searcher-stations.service';

@Injectable({
  providedIn: 'root'
})
export class SearcherStationsProvider implements ISearcherStationsProvider {
  public stationProvider = 'SearcherStationsDefaultProvider';

  private readonly _resourcesDataService = inject(ResourcesDataService);
  private readonly _convertSearcherStations = inject(ConverterSearcherStationsService);
  private readonly _stateManager = inject(StateManagerService);
  private readonly _store = inject(Store<SearcherState>);
  private readonly _injector = inject(Injector);

  public getProviderDestinationsByOrigin(originCode: string): Observable<SearcherStation[]> {
    const providers = this.getProviders(originCode);
    return this.mapProvidersToStations(providers);
  }

  public getProviderOrigins(): Observable<SearcherStation[]> {
    return this.mapAssetsToOrigins(this.getAssets());
  }

  private mapProvidersToStations(
    providers: Observable<{ markets: Market[]; origins: SearcherStation[] }>
  ): Observable<SearcherStation[]> {
    return providers.pipe(
      map(({ markets, origins }) => {
        const originsByMarket = origins.filter((station) => markets.some((market) => station.code === market.toCode));
        return this._convertSearcherStations.mapDestinations(originsByMarket, markets).map((destination) => ({
          ...destination,
          stationProviderName: this.stationProvider,
          enabled: destination.enabled
        }));
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  private getProviders(originCode: string): Observable<{ markets: Market[]; origins: SearcherStation[] }> {
    return combineLatest({
      markets: this._resourcesDataService.getMarkets(originCode),
      origins: this._store.select(stationSearcherSelector.selectOrigins)
    });
  }

  private getAssets(): Observable<{ stations: Station[]; countries: Country[] }> {
    const assets$ = toObservable(this._stateManager._currentState, { injector: this._injector }).pipe(
      map(({ stations, countries }) => ({ stations: stations ?? [], countries: countries ?? [] }))
    );

    return assets$;
  }

  private mapAssetsToOrigins(
    data: Observable<{ stations: Station[]; countries: Country[] }>
  ): Observable<SearcherStation[]> {
    return data.pipe(
      map(({ stations, countries }) => {
        return this._convertSearcherStations.mapOrigins(stations, countries);
      }),
      catchError(() => {
        return of([]);
      })
    );
  }
}
